import React, { useCallback, useRef, useState, useEffect } from 'react';
import AsyncCreatableSelect from 'react-select/async-creatable';
import {
  medicalConditions,
  createMedicalCondition,
  medicalConditionsByIds,
} from '../modules/doctor/services/patient.services';

const ChiefComplaintInput = ({ Form }) => {
  const debounceTimer = useRef(null);
  const [options, setOptions] = useState([]);
  const [fetchedIds, setFetchedIds] = useState(new Set());
  const [isLoadingInitial, setIsLoadingInitial] = useState(true);

  // ✅ Fetch names for IDs only if not already in options
  useEffect(() => {
    const fetchInitialOptions = async () => {
      const symptoms = Form?.values?.symptoms;

      // Fetch labels for symptom IDs already selected
      if (Array.isArray(symptoms) && symptoms.length > 0) {
        const idsToFetch = symptoms.filter((id) => !fetchedIds.has(id));

        if (idsToFetch.length > 0) {
          try {
            const response = await medicalConditionsByIds(idsToFetch);
            const fetched = response?.data || [];

            const fetchedOptions = fetched.map((item) => ({
              value: item._id,
              label: item.name,
            }));

            setOptions((prev) => {
              const existingIds = new Set(prev.map((opt) => opt.value));
              const merged = [
                ...prev,
                ...fetchedOptions.filter((opt) => !existingIds.has(opt.value)),
              ];
              return merged;
            });

            // Track fetched IDs
            setFetchedIds((prev) => new Set([...prev, ...idsToFetch]));
          } catch (err) {
            console.error('Failed to fetch medical conditions by IDs:', err);
          }
        }
      }

      setIsLoadingInitial(false);
    };

    fetchInitialOptions();
  }, [Form?.values?.symptoms, fetchedIds]);

  // ✅ Fetch some default options on mount (popular or common conditions)
  useEffect(() => {
    const fetchDefaultOptions = async () => {
      try {
        // Fetch default/popular medical conditions, e.g., with empty search or special endpoint
        const response = await medicalConditions(''); // empty string to get default list
        const defaultOptions = (response?.data || []).map((item) => ({
          value: item._id,
          label: item.name,
        }));

        setOptions((prev) => {
          const existingIds = new Set(prev.map((opt) => opt.value));
          const merged = [
            ...prev,
            ...defaultOptions.filter((opt) => !existingIds.has(opt.value)),
          ];
          return merged;
        });
      } catch (err) {
        console.error('Failed to fetch default medical conditions:', err);
      }
    };

    fetchDefaultOptions();
  }, []);

  // ✅ Async search with debounce
  const loadOptions = useCallback((inputValue, callback) => {
    if (!inputValue.trim()) {
      callback([]);
      return;
    }

    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    debounceTimer.current = setTimeout(async () => {
      try {
        const response = await medicalConditions(inputValue);
        const data = response?.data || [];

        const newOptions = data.map((item) => ({
          value: item._id,
          label: item.name,
        }));

        setOptions((prev) => {
          const existingIds = new Set(prev.map((opt) => opt.value));
          const merged = [
            ...prev,
            ...newOptions.filter((opt) => !existingIds.has(opt.value)),
          ];
          return merged;
        });

        callback(newOptions);
      } catch (err) {
        console.error('Error fetching medical conditions:', err);
        callback([]);
      }
    }, 500);
  }, []);

  // ✅ Convert _id array to select-compatible format
  const selectedOptions = Array.isArray(Form?.values?.symptoms)
    ? Form.values.symptoms?.map((id) => {
        const found = options.find((opt) => opt.value === id);
        return {
          value: id,
          label: found?.label || id, // fallback to ID if label not found
        };
      })
    : [];

  // ✅ Save only IDs to Formik
  const handleChange = (selected) => {
    const idsOnly = selected.map((item) => item.value);
    Form.setFieldValue('symptoms', idsOnly);
  };

  // ✅ Create and select new condition
  const handleCreate = async (inputValue) => {
    try {
      const response = await createMedicalCondition({ name: inputValue });

      if (response?.data?._id) {
        const newOption = {
          value: response.data._id,
          label: response.data.name,
        };

        setOptions((prev) => [...prev, newOption]);
        setFetchedIds((prev) => new Set(prev).add(newOption.value)); // prevent re-fetch

        const updatedSelected = [...selectedOptions, newOption];
        handleChange(updatedSelected);
      }
    } catch (err) {
      console.error('Error creating medical condition:', err);
    }
  };

  return (
    <div className="mb-3">
      <label className="form-label">Chief Complaint</label>
      <AsyncCreatableSelect
        isMulti
        cacheOptions
        defaultOptions={options}       // show default options on focus
        loadOptions={loadOptions}
        value={selectedOptions}
        onChange={handleChange}
        onCreateOption={handleCreate}
        isLoading={isLoadingInitial}
        placeholder="Search or create chief complaint"
      />
      {Form.touched.symptoms && Form.errors.symptoms && (
        <div className="text-danger">{Form.errors.symptoms}</div>
      )}
    </div>
  );
};

export default ChiefComplaintInput;
